import { Alpha3Code } from 'i18n-iso-countries'
import styled from 'styled-components'

import {
  Badge,
  CardContent,
  ExternalLink,
  FlagAvatar,
  Flex,
  Heading,
} from '@percent/lemonade'

import { useOrganisationProfileContext } from '../../context/OrganisationProfileContext'
import { useTranslation } from '../../hooks/useTranslation'
import { OrganisationProfile } from '../../types/organisationProfile.types'
import { SectionTitle } from '../SectionTitle/SectionTitle'

const DetailsBox = styled.div`
  ${({ theme }) => `
    padding: ${theme.spacing.m};
    border: 1px solid ${theme.semantic.borderDefault};
    border-radius: ${theme.radii.container};
  `}
`

export type OrganisationDetailsProps = {
  organisation: OrganisationProfile['organisation']
}

export function OrganisationDetails({
  organisation,
}: Readonly<OrganisationDetailsProps>) {
  const { t } = useTranslation()
  const { countries } = useOrganisationProfileContext()

  const notAvailable = t('profile.notAvailable')
  const countryName =
    countries.find(({ code }) => code === organisation.countryCode)?.name ??
    organisation.countryCode
  const address = [organisation.address, organisation.postcode]
    .filter(Boolean)
    .join(', ')

  return (
    <Flex direction="column" gap={16}>
      <Heading level="h2">
        <SectionTitle>{t('profile.detailsTitle')}</SectionTitle>
      </Heading>
      <DetailsBox>
        <CardContent title={t('profile.type')}>
          {organisation.types.length > 0 ? (
            <Flex gap={8} wrap="wrap">
              {organisation.types.map((type) => (
                <Badge key={type} variant="interactive">
                  {t(`profile.organisationTypes.${type}`)}
                </Badge>
              ))}
            </Flex>
          ) : (
            notAvailable
          )}
        </CardContent>
        <CardContent title={t('profile.country')}>
          <Flex align="center" gap={8}>
            <FlagAvatar code3={organisation.countryCode as Alpha3Code} />
            <span>{countryName}</span>
          </Flex>
        </CardContent>
        <CardContent title={t('profile.activity')}>
          {organisation.activityTags.length > 0 ? (
            <Flex gap={8} wrap="wrap">
              {organisation.activityTags.map((tag) => (
                <Badge key={tag.id} variant="default" icon="categories">
                  {tag.name}
                </Badge>
              ))}
            </Flex>
          ) : (
            notAvailable
          )}
        </CardContent>
        <CardContent title={t('profile.website')}>
          {organisation.website ? (
            <ExternalLink
              href={organisation.website}
              text={organisation.website}
              hideIcon
            />
          ) : (
            notAvailable
          )}
        </CardContent>
        <CardContent title={t('profile.email')}>
          {organisation.email ? (
            <a href={`mailto:${organisation.email}`}>{organisation.email}</a>
          ) : (
            notAvailable
          )}
        </CardContent>
        <CardContent title={t('profile.address')}>
          {address || notAvailable}
        </CardContent>
        <CardContent title={t('profile.registry')}>
          {organisation.registry?.name ?? notAvailable}
        </CardContent>
        <CardContent title={t('profile.registryId')}>
          {organisation.registry?.registryId ?? notAvailable}
        </CardContent>
      </DetailsBox>
    </Flex>
  )
}
